import { Component, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Tooltip } from 'primeng/tooltip';

import { TranslatePipe, TranslateService } from '../../core/i18n';
import { EstablishmentStore } from '../../features/establishment/store/establishment.store';
import { OnboardingStore } from '../../features/onboarding/store/onboarding.store';
import { SidebarStore } from '../sidebar.store';

interface MenuItem {
  labelKey: string;
  icon: string;
  route: string;
  exact?: boolean;
  requiresAdmin?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe, Tooltip],
  templateUrl: './sidebar.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
})
export class SidebarComponent {
  protected sidebarStore = inject(SidebarStore);
  protected establishmentStore = inject(EstablishmentStore);
  protected translateService = inject(TranslateService);
  private readonly onboardingStore = inject(OnboardingStore);

  /**
   * Whether the user is currently in onboarding mode (establishment incomplete).
   * Only activates when isComplete() === false AND the establishment is not yet active.
   * Active establishments bypass onboarding restrictions entirely.
   */
  protected isOnboarding = computed(
    () =>
      this.onboardingStore.isComplete() === false &&
      !this.establishmentStore.isEstablishmentActive(),
  );

  private readonly menuItemsConfig: MenuItem[] = [
    { labelKey: 'sidebar.dashboard', icon: 'dashboard', route: '/dashboard', exact: true },
    { labelKey: 'sidebar.createLink', icon: 'add', route: '/create-link' },
    { labelKey: 'sidebar.myLinks', icon: 'list', route: '/my-links' },
    {
      labelKey: 'sidebar.transactions',
      icon: 'creditcard',
      route: '/transactions',
      requiresAdmin: true,
    },
    { labelKey: 'sidebar.posTerminals', icon: 'pos', route: '/pos', requiresAdmin: true },
    { labelKey: 'sidebar.endReport', icon: 'report', route: '/end-report', requiresAdmin: true },
  ];

  protected mainMenuItems = computed(() => {
    const isAdmin = this.establishmentStore.isAdmin();
    const onboarding = this.isOnboarding();
    return this.menuItemsConfig.map((item) => ({
      ...item,
      disabled: (onboarding && item.route !== '/dashboard') || (item.requiresAdmin && !isAdmin),
    }));
  });

  protected bottomMenuItems: MenuItem[] = [
    { labelKey: 'sidebar.myProfile', icon: 'user', route: '/profile' },
  ];

  protected disabledTooltip = computed(() =>
    this.isOnboarding()
      ? this.translateService.get('onboarding.completeActivationFirst')
      : this.translateService.get('auth.noPermission'),
  );

  getIcon(icon: string): string {
    const icons: Record<string, string> = {
      dashboard:
        'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
      add: 'M12 4v16m8-8H4',
      list: 'M4 6h16M4 10h16M4 14h16M4 18h16',
      creditcard:
        'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
      pos: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
      user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
      report:
        'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      analytics:
        'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      security:
        'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    };
    return icons[icon] ?? icons['dashboard'];
  }
}
